import { Component, HostListener, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { distinctUntilChanged, exhaustMap, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { appVersion } from '../../version';
import { AppSandbox } from '../app.sandbox';
import { Location } from '@angular/common';
import { BehaviorSubject, iif, Observable, throwError, timer, merge } from 'rxjs';
import { MyRouter } from '../../common/services/router.service';
import { TranslateService } from '@ngx-translate/core';
import { SwUpdateService } from '../external-app-services/sw/sw-update.service';
import { Availability } from '../../common/interfaces/availability.interface';
import { IntercomService } from '../external-app-services/intercom/intercom.service';
import { Title } from '@angular/platform-browser';
import { AppConfigService } from '../services/app-config.service';
import { HotjarService } from '../external-app-services/hotjar/hotjar.service';
import { UsersnapService } from '../external-app-services/usersnap/usersnap.service';
import { environment } from '../../environments/environment';
import { getLanguage, updateLanguage } from '../../common/languages';
import { DateAdapter } from '@angular/material/core';
import { BrowserSupportedService } from '../services/browser-supported.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.container.html',
    styleUrls: ['./app.container.scss']
})
export class AppContainer implements OnInit {
    readonly AVAILABILITY_TIMER: number = 3.6e+6;

    initApp = false;
    bannerText: string;
    browserSupported = true;
    corporateClientName$: Observable<string> = this.appSandbox.corporateClientName$;
    allowedToOrder$: Observable<boolean> = this.appSandbox.allowedToOrder$;
    showFooter$: Observable<boolean> = this.appSandbox.showFooter$;
    isOciUser$: Observable<boolean> = this.appSandbox.isOciUser$;

    constructor(private translateService: TranslateService,
                private customRouter: MyRouter,
                private router: Router,
                private appSandbox: AppSandbox,
                private location: Location,
                private swUpdateService: SwUpdateService,
                private intercomService: IntercomService,
                private titleService: Title,
                private appConfigService: AppConfigService,
                private hotjarService: HotjarService,
                private usersnapService: UsersnapService,
                private browserSupportedService: BrowserSupportedService,
                private dateAdapter: DateAdapter<any>) {
        this.swUpdateService.checkForUpdates();
        this.titleService.setTitle(this.appConfigService.getAppConfig()?.browserTitle);
        this.browserSupported = this.browserSupportedService.isSupported();
        this.redirectPilotUsersToProdUrl();
        this.loadFavicon();
        this.loadLanguage();
    }


    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler() {
        this.appSandbox.getStoreCorporateClientId()
            .subscribe((corporateClientId: number) => {
                if (corporateClientId) {
                    localStorage.setItem('corporate-client', corporateClientId.toString());
                }
            });
    }

    ngOnInit() {
        this.setAppVersionOnAppRoot();
        this.loadFirstPage();
        this.checkForAvailability();
        this.loadExternalApps();
    }

    isUnauthorized() {
        const route = this.location.path();
        return this.isUnauthenticatedRoute(route);
    }

    logout() {
        this.appSandbox.logout();
    }

    // Temp until pilot version is over
    private redirectPilotUsersToProdUrl() {
        if (environment.production && window.location.href.indexOf('pilot.mytvhequipment.com') !== -1) {
            window.location.href = 'https://mytvhequipment.com';
        } else if (environment.production && window.location.href.indexOf('pilot.mymateco.de') !== -1) {
            window.location.href = 'https://mymateco.de';
        } else if (environment.production && window.location.href.indexOf('pilot.mymateco.nl') !== -1) {
            window.location.href = 'https://mymateco.nl';
        }
    }


    private loadExternalApps() {
        this.intercomService.load();
        this.hotjarService.load();
        this.usersnapService.load();
    }

    private setAppVersionOnAppRoot() {
        const appRoots = document.getElementsByTagName('app-root');
        if (appRoots && appRoots.length > 0) {
            appRoots[0].setAttribute('app-version', appVersion);
        }
    }

    private loadLanguage() {
        this.translateService.setDefaultLang('en');
        const lang = getLanguage();
        localStorage.setItem('language', lang);
        updateLanguage(lang, this.appConfigService, this.translateService, this.dateAdapter);
    }

    private loadFavicon() {
        const favicon: Element = document.querySelector('#favicon');
        if (favicon) {
            favicon.setAttribute('href', '/assets/icons/icon-' + this.appConfigService.getAppConfig().opCo + '.ico');
        }
    }

    private checkForAvailability() {
        const availabilityTimer$ = new BehaviorSubject(this.AVAILABILITY_TIMER);

        availabilityTimer$.pipe(
            distinctUntilChanged(),
            switchMap(availabilityTimer =>
                merge(
                    timer(0, availabilityTimer),
                    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
                ).pipe(
                    exhaustMap(() => this.appSandbox.getAvailability())
                )
            )
        ).subscribe({
            next: (availability: Availability) => {
                this.bannerText = availability.bannerText;
                const forceLogin = localStorage.getItem('forceLogin') === 'true';
                if ((availability.status !== 'UP' || this.appConfigService.getAppConfig().maintenanceMode) && !forceLogin) {
                    this.appSandbox.logout();
                    availabilityTimer$.next(5000);
                    this.router.navigate(['/auth/maintenance']);
                } else {
                    if (this.router.url.includes('maintenance')) {
                        this.customRouter.navigateToLogin();
                    }

                    availabilityTimer$.next(availability.orderProcessAvailable ? this.AVAILABILITY_TIMER : 5000);
                }
            }, error: () => availabilityTimer$.next(5000)
        });
    }

    private loadFirstPage() {
        let firstRoute = null;
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationStart),
                take(1),
                map((event: NavigationStart) => event.url),
                tap((url: string) => firstRoute = url),
                mergeMap((url: string) => iif(
                    () => url.includes('/login', 0),
                    throwError(new Error('loadLoginScreen')),
                    this.appSandbox.loginUserWithToken()
                ))
            )
            .subscribe({
                next: () => this.loadAppData(),
                error: () => this.loadLoginScreen(firstRoute)
            });
    }

    private loadAppData() {
        this.appSandbox.loadUserSettings()
            .subscribe(() => {
            this.preloadFinished();
        });
    }

    private loadLoginScreen(firstRoute: string): void {
        if (firstRoute.includes('/instances', 0)) {
            this.preloadFinished();
        } else if (!this.isUnauthenticatedRoute(firstRoute)) {
            this.customRouter.navigateToLogin()
                .then(() => this.preloadFinished());
        } else {
            if (!!localStorage.getItem('corporate-client') && !!localStorage.getItem('token')) {
                this.loadAppData();
                this.customRouter.navigateWithCorporateClient(['/']);
            } else {
                if (localStorage.getItem('token')) {
                    this.appSandbox.logout();
                }
                this.preloadFinished();
            }
        }
    }

    private isUnauthenticatedRoute(route: string): boolean {
        return route.includes('/auth', 0) || route.includes('/instances', 0);
    }

    private preloadFinished() {
        this.initApp = true;
        this.appSandbox.setAppReady();
    }
}
